import { useState, useRef, useEffect } from "react";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  RollbackOutlined,
  DisconnectOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  Row,
  Space,
  Table,
  Tabs,
  Empty,
  Typography,
  theme,
  Checkbox,
  Tooltip,
  Tag,
  message,
  Image,
  Input,
  Modal,
} from "antd";
import moment from "moment-timezone";
import { Container } from "./elements";
import { order } from "../../types";
import TabLabel from "./components/TabLabel";
import cookies from "react-cookies";
import { connectWebSocket } from "./components/websocket";
import { backend } from "../../config/environment";
//import { CREATE_MOCK_ORDER } from "./requests";
//import { useMutation } from "@apollo/client";
import { orderStatus, statusColors } from "../../utils/constants";
import ProductList from "../../components/productsList";
import { useUser } from "../../providers/user";
import { OrdersDrawer } from "./components/OrdersDrawer";
import newOrderSound from "./components/sounds/new-order-alarm-sound.mp3";
import { buildProductList } from "../../utils/functions";
import CompleteModal from "./components/complete-modal";

const { Text } = Typography;
const { TabPane } = Tabs;
const { useToken } = theme;

export default function Orders() {
  const { token } = useToken();
  const userToken = cookies.load("token");
  const [orders, setOrders] = useState<(typeof order.order)[]>([]);
  const [checkedItems, setCheckedItems] = useState<{
    [orderId: string]: { [itemName: string]: boolean };
  }>({});
  const [loading, setLoading] = useState(false);
  const [loadingChangeStatus, setLoadingChangeStatus] = useState<{
    [orderId: string]: boolean;
  }>({});
  const [timeDifferences, setTimeDifferences] = useState<{
    [key: string]: string;
  }>({});
  const [timeDifferencesPickup, setTimeDifferencesPickup] = useState<{
    [key: string]: string;
  }>({});
  const [numberOrder, setNumberOrder] = useState<string>("");
  const [nameClient, setNameClient] = useState<string>("");
  const [connectionState, setConnectionState] = useState<
    "connecting" | "connected" | "disconnected"
  >("connecting");

  const [visibleDrawer, setVisibleDrawer] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderId, setOrderId] = useState<string>(""); const [showInputCompleteModal, setShowInputCompleteModal] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  //const [createMockOrderMutation] = useMutation(CREATE_MOCK_ORDER);

  const { user } = useUser();

  const isLocationOpen = (location: any): boolean => {
    const now = moment().tz("America/Mexico_City");
    const currentDay = now.format("dddd").toLowerCase();

    const nowTime = now.format("HH:mm");

    const holidayToday = location?.operations?.holidays?.find((holiday: any) => {
      const holidayDate = moment(holiday.date).tz("America/Mexico_City").format("MM-DD");
      const todayMMDD = now.format("MM-DD");
      return holiday.repeat
        ? holidayDate === todayMMDD
        : moment(holiday.date).format("YYYY-MM-DD") === now.format("YYYY-MM-DD");
    });

    if (holidayToday) {
      if (holidayToday.isOpen && holidayToday.hours) {
        const openTime = moment(holidayToday.hours.open).tz("America/Mexico_City").format("HH:mm");
        const closeTime = moment(holidayToday.hours.close).tz("America/Mexico_City").format("HH:mm");

        return nowTime >= openTime && nowTime < closeTime;
      } else {
        return false;
      }
    }

    const workingHours = location?.operations?.workingHours?.[currentDay];
    if (!workingHours?.open || !workingHours?.close) return false;

    const openTime = moment(workingHours.open).tz("America/Mexico_City").format("HH:mm");
    const closeTime = moment(workingHours.close).tz("America/Mexico_City").format("HH:mm");

    return nowTime >= openTime && nowTime < closeTime;
  };

  useEffect(() => {
    if (user?.type === "operation" && user?.location) {
      const open = isLocationOpen(user.location);
      if (!open) {
        Modal.info({
          title: "Franquicia cerrada",
          content: "Esta ubicación está cerrada actualmente.",
          okText: "Entendido",
        });
      }
    }
  }, [user]);

  const openDrawer = (order: any) => {
    setSelectedOrder(order);
    setVisibleDrawer(true);
  };

  const rowClassName = (record: any) => {
    const isHighlighted = record.isReturnProgrammed ? "highlight-row" : "";
    return `${isHighlighted} hover:bg-gray-50 transition-colors`; // Combine both classes
  };

  useEffect(() => {
    // Create audio with Web Audio API for maximum volume control
    const audio = new Audio(newOrderSound);
    audio.preload = "auto";
    audio.volume = 1.0; // Explicitly set to maximum volume
    audio.load();
    audioRef.current = audio;

    // Optional: Use Web Audio API to amplify beyond normal limits
    let audioContext: AudioContext | null = null;
    let gainNode: GainNode | null = null;

    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = audioContext.createMediaElementSource(audio);
      gainNode = audioContext.createGain();
      gainNode.gain.value = 2.0; // Amplify by 2x (adjust as needed, 1.0 = normal, 2.0 = double)
      source.connect(gainNode);
      gainNode.connect(audioContext.destination);
    } catch (err) {
      console.warn("Web Audio API not supported, using standard audio:", err);
    }

    const unsubscribe = connectWebSocket(
      (updatedOrder: any) => {
        const a = audioRef.current;
        if (a) {
          try {
            // Resume audio context if suspended (required for some browsers)
            if (audioContext?.state === "suspended") {
              audioContext.resume();
            }
            a.currentTime = 0;
            void a.play();
          } catch (err) {
            console.error("Error playing sound:", err);
          }
        }

        setOrders((prev) => {
          const exists = prev.some((o) => o._id === updatedOrder._id);
          return exists
            ? prev.map((o) => (o._id === updatedOrder._id ? updatedOrder : o))
            : [...prev, updatedOrder];
        });
      },
      (state) => {
        setConnectionState(state);
      },
    ) as (() => void) | undefined;

    return () => {
      // prevent double handlers in Strict Mode + free audio
      unsubscribe?.();
      audio.pause();
      audio.src = ""; // release
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          token: userToken,
          ...(numberOrder !== "" && { numberOrder }),
          ...(nameClient !== "" && { nameClient }),
        }).toString();

        const response = await fetch(
          `${backend.AUTH_URI}/order/getOrders?${queryParams}`,
        );

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setOrders(data);
      } catch (error) {
        console.error("Error obteniendo órdenes:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [userToken, numberOrder, nameClient]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeDifferences(() => {
        const newDifferences: { [key: string]: string } = {};
        orders.forEach((order) => {
          if (order.preparedTime) {
            const duration = moment.duration(
              moment().diff(moment(order.preparedTime)),
            );
            newDifferences[order._id] =
              `${duration.minutes()}m ${duration.seconds()}s`;
          }
        });
        return newDifferences;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [orders]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeDifferencesPickup(() => {
        const newDifferences: { [key: string]: string } = {};
        orders.forEach((order) => {
          if (order.pickupTime) {
            const duration = moment.duration(
              moment(order.pickupTime).diff(moment()),
            );
            const hours = duration.hours();
            const minutes = duration.minutes();
            const seconds = duration.seconds();

            const isNegative = duration.asMilliseconds() < 0;

            if (isNegative) {
              newDifferences[order._id] =
                `Tiempo pasado: -${Math.abs(hours)}h ${Math.abs(minutes)}m ${Math.abs(seconds)}s`;
            } else {
              newDifferences[order._id] =
                `Tiempo restante: ${hours}h ${minutes}m ${seconds}s`;
            }
          }
        });
        return newDifferences;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [orders]);

  const handleCheckboxChange = (
    orderId: string,
    itemName: string,
    index: number,
  ) => {
    setCheckedItems((prev) => {
      const updated = { ...prev };
      if (!updated[orderId]) updated[orderId] = {};
      updated[orderId][`${itemName}-${index}`] =
        !updated[orderId][`${itemName}-${index}`];
      return { ...updated };
    });
  };

  const isOrderComplete = (
    orderId: string,
    items: { name: string; quantity: number }[],
  ) => {
    const orderCheckedItems = checkedItems[orderId] || {};
    return items
      .flatMap((item) => Array(item.quantity).fill(item.name))
      .every((name, index) => orderCheckedItems[`${name}-${index}`]);
  };

  const handleMarkPrepared = async (orderId: string) => {
    setLoadingChangeStatus((prev) => ({ ...prev,
      [orderId]: true }));

    const order = orders.find((o) => o._id === orderId);
    if (
      order &&
      isOrderComplete(orderId, buildProductList(order.lineItems))
    ) {
      try {
        await fetch(
          `${backend.AUTH_URI}/order/updateOrder?token=${userToken}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              id: orderId,
              status: orderStatus.prepared.value,
            }),
          },
        );

        message.success("Orden Actualizada.");
        setLoadingChangeStatus((prev) => ({ ...prev,
          [orderId]: false }));
      } catch (err) {
        console.log(err);
        setLoadingChangeStatus((prev) => ({ ...prev,
          [orderId]: false }));
        message.error("Hubo un error.");
      }
    } else {
      setLoadingChangeStatus((prev) => ({ ...prev,
        [orderId]: false }));
    }
  };

  const handleMarkComplete = async (orderId: string, internalReference: string) => {
    setLoadingChangeStatus((prev) => ({ ...prev,
      [orderId]: true }));

    try {
      await fetch(`${backend.AUTH_URI}/order/updateOrder?token=${userToken}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: orderId,
          status: orderStatus.complete.value,
          internalReference: internalReference,
        }),
      });

      message.success("Orden Actualizada.");
      setLoadingChangeStatus((prev) => ({ ...prev,
        [orderId]: false }));
    } catch (err) {
      console.log(err);
      setLoadingChangeStatus((prev) => ({ ...prev,
        [orderId]: false }));
      message.error("Hubo un error.");
    } finally {
      setLoadingChangeStatus((prev) => ({ ...prev,
        [orderId]: false }));
    }
  };

  const handleBackToProgrammed = async (orderId: string, lineItems?: any) => {
    setLoadingChangeStatus((prev) => ({ ...prev,
      [orderId]: true }));

    try {
      await fetch(`${backend.AUTH_URI}/order/updateOrder?token=${userToken}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: orderId,
          status: orderStatus.programmed.value,
          lineItems: lineItems,
        }),
      });

      message.success("Orden Actualizada.");
      setLoadingChangeStatus((prev) => ({ ...prev,
        [orderId]: false }));
    } catch (err) {
      console.log(err);
      setLoadingChangeStatus((prev) => ({ ...prev,
        [orderId]: false }));
      message.error("Hubo un error.");
    } finally {
      setLoadingChangeStatus((prev) => ({ ...prev,
        [orderId]: false }));
    }
  };

  const scheduledColumns = [
    {
      title: "# Orden",
      key: "reference",
      width: 110,
      render: (row: any) => (
        <div>
          <Text strong style={{ color: token.colorPrimary }}>
            {row?.reference}
          </Text>
          <br />
          <Text style={{ color: "#8c8c8c" }}>
            {row?.client?.user.firstName} {row?.client?.user.lastName}
          </Text>
        </div>
      ),
    },
    {
      title: "Tiempo",
      dataIndex: "pickupTime",
      key: "pickupTime",
      render: (text: string) => (
        <Tag color={"green"}>
          <ClockCircleOutlined
            className="text-gray-400"
            style={{ marginRight: 5 }}
          />
          {moment(text).format("HH:mm")}
        </Tag>
      ),
    },
    {
      title: "Productos",
      dataIndex: "lineItems",
      key: "lineItems",
      width: 800, // Adjust width so only 3 items fit
      render: (
        lineItems: (typeof order.order)["lineItems"],
        record: typeof order.order,
      ) => {
        const productList = lineItems?.flatMap((item) => [
          ...Array(item.qty).fill({
            name:
              item?.product?.product?.name +
              (item?.product?.title ? ` (${item?.product?.title})` : ""),
            image: item?.product?.img,
            productId: item?.product?.product?._id,
          }),
          ...(item.complements?.flatMap((complement) =>
            Array(complement.quantity).fill({
              name: `${complement.variant.title ? complement.variant.title : complement?.variant?.product?.name} (Complemento)`,
              image: complement.variant.img,
              productId: complement?._id,
            })) || []),
          ...(item.beverages?.flatMap((beverage) =>
            Array(beverage.quantity).fill({
              name: `${beverage.variant.title ? beverage.variant.title : beverage.variant.product?.name} (Bebida)`,
              image: beverage.variant.img,
              productId: beverage?._id,
            })) || []),
          ...(item.extras?.flatMap((extra) =>
            Array(extra.quantity).fill({
              name: `${extra.variant.title ? extra.variant.title : extra.variant.product?.name} (Extra)`,
              image: extra.variant.img,
              productId: extra?._id,
            })) || []),
        ]);

        const isReturnProgrammed = record.isReturnProgrammed;
        const pendingVariants = new Map(
          record.pendingsLineItems?.map((item) => [item.variant, item.qty]),
        );

        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              maxWidth: "600px",
            }}
          >
            {productList.map((item, index) => {
              let backgroundColor = "transparent";
              let borderColor = "#ddd";
              const pendingQty = pendingVariants.get(item.productId) || 0;
              const qtyToHighlight = pendingQty;

              if (isReturnProgrammed) {
                if (qtyToHighlight > 0) {
                  backgroundColor = "rgba(255, 0, 0, 0.1)"; // Red for pending items
                  borderColor = "rgba(255, 0, 0, 0.5)"; // Red border
                  pendingVariants.set(
                    item.productId,
                    pendingQty - qtyToHighlight,
                  ); // Reduce the pending qty
                } else {
                  backgroundColor = "rgba(0, 128, 0, 0.1)"; // Green for non-pending items
                  borderColor = "rgba(0, 128, 0, 0.5)"; // Green border
                }
              }

              return (
                <div
                  key={`${item.name}-${index}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px",
                    borderRadius: "8px",
                    border: `1px solid ${borderColor}`,
                    backgroundColor,
                    minWidth: "180px",
                    flex: "1 1 30%",
                    maxWidth: "33%",
                  }}
                >
                  <Checkbox
                    style={{
                      transform: "scale(1.1)",
                      marginRight: "4px",
                    }}
                    checked={
                      checkedItems[record._id]?.[`${item.name}-${index}`] ||
                      false
                    }
                    onChange={() =>
                      handleCheckboxChange(record._id, item.name, index)}
                  />

                  {item.image && (
                    <Image
                      src={item.image}
                      width={40}
                      height={40}
                      style={{
                        objectFit: "cover",
                        borderRadius: "6px",
                      }}
                      preview={false}
                    />
                  )}

                  <Text
                    strong
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.2",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      textAlign: "left",
                    }}
                  >
                    {item.name}
                  </Text>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: "Acción",
      width: 100,
      render: (_: unknown, record: typeof order.order) => (
        <Tooltip
          title={
            !isOrderComplete(record._id, buildProductList(record.lineItems))
              ? "Prepare la Orden"
              : null
          }
        >
          <Button
            type="primary"
            loading={loadingChangeStatus[record._id] || false}
            onClick={() => handleMarkPrepared(record._id)}
            disabled={
              !isOrderComplete(record._id, buildProductList(record.lineItems))
            }
          >
            Marcar Listo
          </Button>
        </Tooltip>
      ),
    },
  ];

  const preparedColumns = [
    {
      title: "# Orden",
      key: "reference",
      render: (row: any) => (
        <div>
          <Text strong style={{ color: token.colorPrimary }}>
            {row?.reference}
          </Text>
          <br />
          <Text style={{ color: "#8c8c8c" }}>
            {row?.client?.user.firstName} {row?.client?.user.lastName}
          </Text>
        </div>
      ),
    },
    {
      title: "Tiempo Preparado",
      key: "preparedTime",
      render: (row: typeof order.order) => (
        <Tag color="green">
          <CheckCircleOutlined
            className="text-gray-400"
            style={{ marginRight: 5 }}
          />

          {timeDifferences[row._id] || "Calculando..."}
        </Tag>
      ),
    },
    {
      title: "Fecha Programada",
      key: "pickupTime",
      render: (row: typeof order.order) => {
        return (
          <Space direction="vertical">
            <Tag color="blue" className="font-medium text-gray-700">
              {moment(row.pickupTime).format("lll")}
            </Tag>
            <Text className="font-medium text-gray-700">
              {timeDifferencesPickup[row._id] || "Calculando..."}
            </Text>
          </Space>
        );
      },
    },
    {
      title: "Productos",
      dataIndex: "lineItems",
      key: "lineItems",
      render: (lineItems: (typeof order.order)["lineItems"]) => {
        return <ProductList lineItems={lineItems} />;
      },
    },
    {
      title: "Acción",
      width: 250,
      render: (_: unknown, record: typeof order.order) => (
        <Row gutter={16} style={{ alignItems: "center" }}>
          <Col>
            <Tooltip title="Volver a preparar">
              <Button
                danger={true}
                type="primary"
                onClick={() => openDrawer(record)}
              >
                <RollbackOutlined />
              </Button>
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title="Orden completa">
              <Button
                type="primary"
                onClick={() => {
                  setOrderId(record._id);
                  setShowInputCompleteModal(true);
                }}
                disabled={record.status !== "prepared"}
              >
                Marcar Completa
              </Button>
            </Tooltip>
          </Col>
        </Row>
      ),
    },
  ];

  return (
    <div style={{ overflowY: "scroll",
      height: "100vh" }}>
      <Container>
        {connectionState !== "connected" && (
          <Alert
            message={
              connectionState === "connecting" ? (
                <Space>
                  <LoadingOutlined spin />
                  Conectando al servidor en tiempo real...
                </Space>
              ) : (
                <Space>
                  <DisconnectOutlined />
                  Conexión perdida. Reintentando automáticamente...
                </Space>
              )
            }
            type={connectionState === "connecting" ? "info" : "warning"}
            showIcon={false}
            banner
            style={{ marginBottom: 16 }}
          />
        )}
        <Row justify="space-between" align="middle">
          <Col>
            {/* <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={async () => await createMockOrderMutation()}
            >
              Crear Orden
            </Button> */}
          </Col>
          <Col style={{ marginLeft: "auto",
            display: "flex",
            gap: "8px" }}>
            <Tooltip title="Buscar por numero de orden">
              <Input
                allowClear
                placeholder="Número de Orden"
                value={numberOrder}
                onChange={(e) => setNumberOrder(e.target.value)}
              />
            </Tooltip>
            <Tooltip title="Buscar por numero de orden">
              <Input
                allowClear
                placeholder="Nombre del Cliente"
                value={nameClient}
                onChange={(e) => setNameClient(e.target.value)}
              />
            </Tooltip>
          </Col>
        </Row>
        <Tabs
          defaultActiveKey="1"
          animated
          tabBarStyle={{
            padding: "0 1px",

            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
          className="bg-white rounded-lg px-4 pt-2 shadow-soft"
        >
          <TabPane
            tab={
              <TabLabel
                count={
                  orders.filter((order) => order.status === "programmed")
                    .length
                }
                label="Programado"
                color={statusColors.preparing}
              />
            }
            key="1"
          >
            <div style={{ maxHeight: '70vh',
              overflowY: 'auto' }}>
              <Table
                loading={loading}
                columns={scheduledColumns}
                dataSource={orders
                  .filter((order) => order.status === "programmed")
                  .sort(
                    (a, b) =>
                      (b.isReturnProgrammed ? 1 : 0) -
                      (a.isReturnProgrammed ? 1 : 0),
                  )}
                rowKey="id"
                locale={{
                  emptyText: (
                    <Empty
                      image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
                      imageStyle={{
                        height: 60,
                      }}
                      description={
                        <span>
                          No hay <a>Ordenes</a> pendientes
                        </span>
                      }
                    ></Empty>
                  ),
                }}
                pagination={false}
                className="rounded-lg overflow-hidden shadow-soft"
                bordered={false}
                rowClassName={rowClassName}
              />
            </div>
          </TabPane>
          <TabPane
            tab={
              <TabLabel
                count={
                  orders.filter((order) => order.status === "prepared").length
                }
                label="Preparado"
                color={statusColors.ready}
              />
            }
            key="2"
          >
            <div style={{ maxHeight: '70vh',
              overflowY: 'auto' }}>
              <Table
                loading={loading}
                columns={preparedColumns}
                dataSource={orders.filter(
                  (order) => order.status === "prepared",
                )}
                rowKey="id"
                pagination={false}
                className="rounded-lg overflow-hidden shadow-soft"
                bordered={false}
                rowClassName="hover:bg-gray-50 transition-colors"
              />
            </div>
            {selectedOrder && (
              <OrdersDrawer
                visible={visibleDrawer}
                setVisible={setVisibleDrawer}
                order={selectedOrder}
                backToProgrammed={handleBackToProgrammed}
              />
            )}
          </TabPane>
        </Tabs>
      </Container>
      <CompleteModal
        showModal={showInputCompleteModal}
        setShowModal={setShowInputCompleteModal}
        handleMarkComplete={handleMarkComplete}
        orderId={orderId}
      />
    </div>
  );
}
